import { useCallback, useSyncExternalStore } from "react";

export type Theme = "light" | "dark" | "system";
const KEY = "theme";
const listeners = new Set<() => void>();

function read(): Theme {
	try {
		const t = localStorage.getItem(KEY);
		return t === "light" || t === "dark" ? t : "system";
	} catch {
		return "system";
	}
}

/** Writes `data-theme` on <html> (tokens.css switches on it); "system" follows prefers-color-scheme. */
export function applyTheme(theme: Theme): void {
	const root = document.documentElement;
	if (theme === "system") delete root.dataset.theme;
	else root.dataset.theme = theme;
	try {
		localStorage.setItem(KEY, theme);
	} catch {
		// private mode: the choice lasts for the page
	}
	for (const l of listeners) l();
}

const subscribe = (l: () => void) => {
	listeners.add(l);
	return () => {
		listeners.delete(l);
	};
};

/** The chosen theme and its setter, shared by every ThemeToggle on the page. */
export function useTheme(): [Theme, (theme: Theme) => void] {
	const theme = useSyncExternalStore(subscribe, read, () => "system" as Theme);
	const set = useCallback((t: Theme) => applyTheme(t), []);
	return [theme, set];
}
